"use client";
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { ChevronLeft, ShoppingCart, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ProductContext } from '../context/ProductContext';
import { useCart } from '../context/CartContext';
import { useCategories } from '../context/CategoriesContext';
import { useMemo } from 'react';
import toast from 'react-hot-toast';

function CatCarousel() {
  const { products, productsLoading } = useContext(ProductContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);
  const touchStartRef = useRef(0);

  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { categories = [] } = useCategories();

  const AUTO_PLAY_DELAY = 10000; // 10 seconds

  // Create category mapping
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[String(c.id)] = c.title;
    });
    return map;
  }, [categories]);

  // Create infinite loop by duplicating items
  const getInfiniteItems = useCallback(() => {
    if (!products || products.length === 0) return [];

    // Duplicate items for seamless infinite scroll
    const duplicateCount = Math.min(3, products.length); // Show 3 items on each side
    const startItems = products.slice(-duplicateCount);
    const endItems = products.slice(0, duplicateCount);

    return [...startItems, ...products, ...endItems];
  }, [products]);

  const infiniteItems = getInfiniteItems();
  const duplicateCount = Math.min(3, products.length);

  // Initialize with offset for duplicated items
  useEffect(() => {
    if (products.length > 0) {
      setCurrentIndex(duplicateCount);
    }
  }, [products.length, duplicateCount]);

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && !isDragging && !isTransitioning) {
      autoPlayRef.current = setInterval(() => {
        goToNext();
      }, AUTO_PLAY_DELAY);
    } else {
      clearInterval(autoPlayRef.current);
    }

    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying, isDragging, isTransitioning]);

  // Handle infinite loop transitions
  const handleInfiniteTransition = useCallback(() => {
    if (currentIndex >= infiniteItems.length - duplicateCount) {
      // At the end, jump to the beginning
      setTimeout(() => {
        setIsTransitioning(true);
        setCurrentIndex(duplicateCount);
        setTimeout(() => setIsTransitioning(false), 50);
      }, 700);
    } else if (currentIndex < duplicateCount) {
      // At the beginning, jump to the end
      setTimeout(() => {
        setIsTransitioning(true);
        setCurrentIndex(infiniteItems.length - duplicateCount - 1);
        setTimeout(() => setIsTransitioning(false), 50);
      }, 700);
    }
  }, [currentIndex, infiniteItems.length, duplicateCount]);

  useEffect(() => {
    handleInfiniteTransition();
  }, [currentIndex, handleInfiniteTransition]);

  // Navigation functions
  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setCurrentIndex(prev => prev + 1);
  }, [isTransitioning]);

  const goToPrev = useCallback(() => {
    if (isTransitioning) return;
    setCurrentIndex(prev => prev - 1);
  }, [isTransitioning]);

  const goToSlide = useCallback((index) => {
    if (isTransitioning) return;
    // Convert actual product index to infinite items index
    setCurrentIndex(index + duplicateCount);
  }, [duplicateCount, isTransitioning]);

  // Get the actual product index for dots indicator
  const getActualIndex = useCallback(() => {
    if (products.length === 0) return 0;
    const actualIndex = (currentIndex - duplicateCount) % products.length;
    return actualIndex < 0 ? products.length + actualIndex : actualIndex;
  }, [currentIndex, duplicateCount, products.length]);

  // Touch/Mouse drag handlers
  const handleDragStart = (clientX) => {
    setIsDragging(true);
    setDragStart(clientX);
    setIsAutoPlaying(false);
  };

  const handleDragMove = (clientX) => {
    if (!isDragging) return;
    const offset = clientX - dragStart;
    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    const threshold = 100;
    if (dragOffset > threshold) {
      goToPrev();
    } else if (dragOffset < -threshold) {
      goToNext();
    }

    setIsDragging(false);
    setDragOffset(0);
    setIsAutoPlaying(true);
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleDragMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Mouse event handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleDragEnd();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsAutoPlaying(!isAutoPlaying);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrev, goToNext, isAutoPlaying]);

  // Mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const getItemScale = (index) => {
    const distance = Math.abs(index - currentIndex);
    if (distance === 0) return 1.1; // Center item - reduced from 1.2
    if (distance === 1) return 0.85; // Adjacent items - reduced from 0.9
    return 0.65; // Far items - reduced from 0.7
  };

  const getItemOpacity = (index) => {
    const distance = Math.abs(index - currentIndex);
    if (distance === 0) return 1; // Center item
    if (distance === 1) return 0.8; // Adjacent items
    if (distance === 2) return 0.6; // Second adjacent
    return 0.3; // Far items
  };

  const getItemZIndex = (index) => {
    const distance = Math.abs(index - currentIndex);
    return 10 - distance;
  };

  const getItemSpacing = (index) => {
    const distance = Math.abs(index - currentIndex);
    const baseSpacing = window.innerWidth < 768 ? 280 : 320; // Reduced spacing

    // Add extra spacing for non-center items to reduce congestion
    if (distance === 0) return baseSpacing; // Center item
    if (distance === 1) return baseSpacing + 15; // Adjacent items get more space - reduced
    return baseSpacing + 15; // Far items get even more space - reduced
  };

  if (productsLoading) {
    return <div>Loading products...</div>;
  }

  if (!products || products.length === 0) {
    return <div>No products available</div>;
  }

  return (
    <section
      className="relative overflow-hidden pt-3 pb-3 mt-3 w-full bg-gradient-to-br from-orange-300 via-orange-200 to-amber-200 rounded-2xl shadow-md max-md:max-w-full"
      aria-label="Featured products carousel"
      onMouseEnter={() => setIsAutoPlaying(true)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Header */}
      <div className="relative z-10 text-center mb-3 px-4">
        <h2 className="text-xl md:text-2xl font-bold text-zinc-800 mb-1">
          Featured Product Showcase
        </h2>
        <p className="text-zinc-600 text-sm">
          Discover our trending items and exclusive collections
        </p>
      </div>

      {/* Carousel Container */}
      <div
        ref={carouselRef}
        className="relative h-[300px] md:h-[320px] lg:h-[350px] overflow-visible cursor-grab active:cursor-grabbing px-6"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        style={{ userSelect: 'none' }}
      >
        {/* Carousel Items */}
        <div className="relative w-full h-full flex items-center justify-center">
          {infiniteItems.map((item, index) => {
            const offset = index - currentIndex;
            const scale = getItemScale(index);
            const opacity = getItemOpacity(index);
            const zIndex = getItemZIndex(index);
            const spacing = getItemSpacing(index);

            return (
              <div
                key={`${item.id}-${index}`}
                className={`absolute ${isTransitioning ? '' : 'transition-all duration-700 ease-out'}`}
                style={{
                  transform: `translateX(${offset * spacing + dragOffset}px) scale(${scale})`,
                  opacity: opacity,
                  zIndex: zIndex,
                  pointerEvents: Math.abs(offset) <= 2 ? 'auto' : 'none'
                }}
              >
                <CarouselItem
                  item={item}
                  isCenter={index === currentIndex}
                  isMuted={isMuted}
                  onClick={() => {
                    if (index === currentIndex) {
                      // If clicking center item, navigate to product
                      navigate(`/product/${item.id}`);
                    } else {
                      // If clicking side item, make it center
                      setCurrentIndex(index);
                    }
                  }}
                  navigate={navigate}
                  addToCart={addToCart}
                  categoryMap={categoryMap}
                />
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrev}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-zinc-800 rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-400"
          aria-label="Previous item"
        >
          <ChevronLeft size={20} />
        </button>

        <button
          onClick={goToNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-zinc-800 rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-400"
          aria-label="Next item"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Controls */}
      <div className="relative z-10 flex items-center justify-center gap-4 mt-3 px-4">
        {/* Dots Indicator */}
        <div className="flex gap-1.5">
          {products.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                index === getActualIndex()
                  ? 'bg-zinc-800 scale-125'
                  : 'bg-zinc-400 hover:bg-zinc-600'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Play/Pause Button */}
        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className="bg-white/90 hover:bg-white text-zinc-800 rounded-full p-1.5 shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-400"
          aria-label={isAutoPlaying ? 'Pause autoplay' : 'Start autoplay'}
        >
          {isAutoPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>

        {/* Mute/Unmute Button */}
        <button
          onClick={() => setIsMuted(!isMuted)}
          className="bg-white/90 hover:bg-white text-zinc-800 rounded-full p-1.5 shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-400"
          aria-label={isMuted ? 'Unmute videos' : 'Mute videos'}
        >
          {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      </div>

      {/* Progress Bar */}
      <div className="relative z-10 mt-2 mx-auto w-48 h-0.5 bg-white/30 rounded-full overflow-hidden">
        <div
          className="h-full bg-zinc-800 rounded-full transition-all duration-100 ease-linear"
          style={{
            width: isAutoPlaying ? '100%' : '0%',
            animation: isAutoPlaying ? `progress ${AUTO_PLAY_DELAY}ms linear infinite` : 'none'
          }}
        />
      </div>

      {/* CSS for progress animation */}
      <style jsx>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}

// Individual Carousel Item Component
const CarouselItem = ({ item, isCenter, isMuted, onClick, navigate, addToCart, categoryMap }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // Calculate price display logic (same as ProductGrid)
  const originalPrice = Number(item.price) || 0;
  const offerPrice = Number(item.offerPrice) || 0;

  // Determine if there's a valid offer
  const hasOffer = offerPrice > 0 && offerPrice < originalPrice;

  // Determine the display price (use offer price if available, otherwise original price)
  const displayPrice = hasOffer ? offerPrice : originalPrice;

  const handleCartClick = (e) => {
    e.stopPropagation();
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      navigate("/signin");
      return;
    }

    // Get minimum order quantity
    const minimumOrderQty = item?.minimumOrderQty || item?.min_order_qty || 1;

    // Get category name from categoryMap or fallback to categoryName
    const categoryDisplayName = categoryMap[String(item.categoryId)] || item.categoryName || 'Uncategorized';

    try {
      addToCart(
        {
          ...item,
          price: displayPrice,
          offerPrice: displayPrice,
          categoryId: categoryDisplayName, // Send category name instead of ID
          min_order_qty: minimumOrderQty,
          minimumOrderQty: minimumOrderQty
        },
        minimumOrderQty, // Use minimum order quantity instead of 1
        { personalizedText: "Custom Text", color: "red" }
      );

      // Show success toast with quantity information
      toast.success(`${minimumOrderQty > 1 ? `${minimumOrderQty} × ` : ''}${item.name} added to cart!`, {
        icon: <ShoppingCart size={16} className="text-green-600" />,
        duration: 2000,
      });

      navigate("/cart");
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  return (
    <div
      className={`relative transition-all duration-500 cursor-pointer ${
        isCenter
          ? 'w-64 md:w-72'
          : 'w-48 md:w-56'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {/* Video/Image Container */}
      <div className={`relative overflow-hidden rounded-md shadow-xl mb-2 ${
        isCenter
          ? 'h-40 md:h-48'
          : 'h-32 md:h-40'
      }`}>
        {item.videoUrl && isCenter ? (
          <video
            src={item.videoUrl}
            className="w-full h-full object-cover bg-black"
            autoPlay
            loop
            muted={isMuted}
            playsInline
            controls={false}
            onCanPlay={() => setIsVideoLoaded(true)}
          />
        ) : (
          <img
            src={item.image}
            alt={item.name}
            className={`w-full h-full object-contain bg-white transition-transform duration-700 ${
              isHovered ? 'scale-105' : 'scale-100'
            }`}
            loading="lazy"
          />
        )}

        {/* Hover Overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t from-black/30 to-transparent transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`} />

        {/* Category Badge */}
        <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold shadow-lg">
          {categoryMap[String(item.categoryId)] || item.categoryName || 'Uncategorized'}
        </div>

        {/* Video Controls */}
        {item.videoUrl && isCenter && (
          <div className="absolute bottom-2 right-2 flex gap-1">
            <button className="bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70 transition-colors">
              <Play size={12} />
            </button>
          </div>
        )}

        {/* Special Featured Badge */}
        {item.isMainFeature && isCenter && (
          <div className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold transform rotate-12 shadow-lg">
            FEATURED
          </div>
        )}
      </div>

      {/* Content Card */}
      <div className="bg-white rounded-md p-2.5 flex flex-col justify-between min-h-[80px]">
        <div>
          <h3 className={`font-bold text-zinc-800 mb-1 line-clamp-2 leading-tight ${
            isCenter ? 'text-sm' : 'text-xs'
          }`}>
            {item.name}
          </h3>

          <div className="flex items-center gap-2 mb-1.5">
            {/* Only show original price if there's a valid offer */}
            {hasOffer && (
              <span className="text-xs text-gray-400 line-through">
                ₹{originalPrice}
              </span>
            )}
            <span className={`font-bold text-orange-600 ${
              isCenter ? 'text-base' : 'text-sm'
            }`}>
              ₹{displayPrice}
            </span>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={handleCartClick}
          className={`w-full bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-sm transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
            isCenter ? "py-1.5 text-xs" : "py-1 text-xs"
          }`}
        >
          {isCenter && item.isMainFeature ? "Watch Demo" : "Add to Cart"}
        </button>
      </div>
    </div>
  );
};

export default CatCarousel;
